import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Resource } from 'app/shared/acl/acl.model';
import { PipesModule } from '../pipes';
import { UiModule } from '../ui';


type Item = {
    key: string;
    title: string;
    selected: boolean;
    aclResourceName?: Resource;
    optionsComponent?: Type<any>;
    options?: any;
}


export type Group = {
    key: string;
    title: string;
    aclResourceName?: Resource;
    items: Item[];
    collapsed: boolean;
}


@Component({
    selector: 'options-group',
    imports: [
        UiModule,
        PipesModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    styles: [
        `
        .options {
            background: #EEEEEE;
            padding: 0.5rem 0 0.5rem 2rem;
        }
        `,
    ],
    template: `
        <div class="card mb-3" [formGroup]="form">
            <div
                [attr.tabindex]="collapsible ? '' : null"
                (click)="changeCollapsed(!collapsed)"
                class="card-header d-flex align-items-baseline"
                [class.cursor-pointer]="collapsible"
                [class.border-bottom-0]="collapsible && collapsed">
                <h4 class="mb-0">{{ title }}</h4>
                @if (selected()) {
                    <ui-badge color="primary" class="ml-2" text="Selected"></ui-badge>
                }
                @if (collapsible) {
                    <span class="btn btn-transparent ml-auto">
                        <i class="fa" [class.fa-chevron-down]="collapsed" [class.fa-chevron-up]="!collapsed"></i>
                    </span>
                }
            </div>
            @if (!collapsed || !collapsible) {
                <div animate.enter="slide-up-down-enter" animate.leave="slide-up-down-leave" class="list-group list-group-flush" [formGroupName]="key">
                @for (item of items; track item.key) {
                    <div animate.enter="slide-up-down-enter" animate.leave="slide-up-down-leave">
                        <div class="list-group-item">
                            <ui-checkbox
                                [ngModel]="getControl(key, item.key).enabled"
                                (ngModelChange)="select(getControl(key, item.key), $event)"
                                [ngModelOptions]="{ standalone: true }">
                                {{ item.title }}
                            </ui-checkbox>
                        </div>
                        @if (item.optionsComponent && getControl(key, item.key).enabled) {
                            <div animate.enter="slide-up-down-enter" animate.leave="slide-up-down-leave" class="options">
                                <ng-container [componentOutlet]="item.optionsComponent" [formControlName]="item.key"></ng-container>
                            </div>
                        }
                    </div>
                }
                </div>
            }
        </div>
    `,
})
export class OptionGroupComponent {

    @Input() form: FormGroup;

    @Input() key: string;

    @Input() title: string;

    @Input() items: Group['items'];

    @Input() collapsible: boolean;

    @Input() collapsed: boolean;

    @Output() collapsedChange = new EventEmitter<boolean>();

    getControl(groupKey: string, itemKey: string) {
        return (this.form.controls[groupKey] as FormGroup).controls[itemKey] as FormControl;
    }

    select(control: FormControl, value: boolean) {
        if (value) {
            control.enable();
        } else {
            control.disable();
        }
    }

    selected() {
        return this.items.some(i => (this.form.controls[this.key] as FormGroup)?.controls[i.key]?.enabled);
    }

    changeCollapsed(collapsed: boolean) {
        if (this.collapsible) {
            this.collapsedChange.emit(collapsed);
        }
    }
}


@Component({
    selector: 'options-layout',
    imports: [
        PipesModule,
        OptionGroupComponent,
    ],
    template: `
        <div class="row">
            @for (columns of (groups | acl:'aclResourceName' | filter:nonEmptyGroup | orderBy:'title' | split:3:1); track $index) {
                <div class="col-lg-4 col-md-6 col-12">
                    @for (group of $any(columns); track group.key) {
                        @let items = ($any(group.items) | acl:'aclResourceName' | orderBy:'title' | search:'title':searchText);
                        @if (items.length > 0) {
                            <options-group
                                animate.enter="slide-up-down-enter"
                                animate.leave="slide-up-down-leave"
                                [form]="form"
                                [key]="group.key"
                                [title]="group.title"
                                [items]="$any(items)"
                                [collapsible]="!searchText"
                                [collapsed]="group.collapsed"
                                (collapsedChange)="changeCollapsed(group, $event)">
                            </options-group>
                        }
                    }
                </div>
            }
        </div>
    `,
})
export class OptionsLayoutComponent implements OnInit {

    @Input() groups: Group[];

    @Input() searchText: string;

    @Output() changes = new EventEmitter();

    form: FormGroup;

    private destroyRef = inject(DestroyRef);

    ngOnInit() {
        this.form = new FormGroup(Object.fromEntries(this.groups.map((g: Group) => [
            g.key,
            new FormGroup(Object.fromEntries(g.items.map(i => [
                i.key,
                new FormControl({ value: i.options, disabled: !i.selected }),
            ])))
        ])));
        this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => this.onFormChanges(value));
        this.onFormChanges(this.form.value);
    }

    nonEmptyGroup(group: Group) {
        return group.items.length > 0;
    }

    private onFormChanges(value: Record<string, string>) {
        // prevent form from returning all values when all controls are disabled (default behavior)
        this.changes.emit(this.form.enabled ? value : {});
    }

    changeCollapsed(group: Group, collapsed: boolean) {
        this.groups = this.groups.map(g => g.key === group.key ? ({ ...g, collapsed }) : g);
    }
}
